import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, Package, Truck, XCircle } from "lucide-react";

export default function KitCheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(false);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setError(true);
      return;
    }
    const poll = async () => {
      try {
        const r = await api.get(`/kits/orders/${sessionId}/status`);
        setStatus(r.data);
        if (r.data.payment_status === "paid") return;
        setAttempts((a) => a + 1);
      } catch {
        setError(true);
      }
    };
    poll();
    const iv = setInterval(() => {
      if (attempts >= 20) {
        clearInterval(iv);
        return;
      }
      poll();
    }, 2500);
    return () => clearInterval(iv);
     
  }, [sessionId]);

  if (error || !sessionId) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16 text-center" data-testid="kit-success-error">
        <XCircle className="h-16 w-16 mx-auto text-red-600 mb-4" />
        <h1 className="font-display text-2xl font-bold">Session introuvable</h1>
        <p className="mt-2 text-sm text-muted-foreground">Nous n'avons pas pu retrouver votre commande. Contactez-nous à contact@doctospa.fr.</p>
        <Link to="/kits"><Button className="mt-6 rounded-full">Retour à la boutique</Button></Link>
      </div>
    );
  }

  if (!status || status.payment_status === "initiated") {
    return (
      <div className="mx-auto max-w-2xl px-6 py-20 text-center" data-testid="kit-success-loading">
        <Loader2 className="h-16 w-16 mx-auto text-primary animate-spin mb-4" />
        <h1 className="font-display text-xl font-bold">Confirmation du paiement…</h1>
        <p className="mt-2 text-sm text-muted-foreground">Cela ne prend que quelques secondes.</p>
      </div>
    );
  }

  if (status.payment_status !== "paid") {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16 text-center" data-testid="kit-success-failed">
        <XCircle className="h-16 w-16 mx-auto text-red-600 mb-4" />
        <h1 className="font-display text-2xl font-bold">Paiement non finalisé</h1>
        <p className="mt-2 text-sm text-muted-foreground">Statut : {status.payment_status}</p>
        <Link to="/kits"><Button className="mt-6 rounded-full">Réessayer</Button></Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-16" data-testid="kit-success-page">
      <Card className="p-10 text-center border-2 border-emerald-200 bg-emerald-50">
        <CheckCircle2 className="h-20 w-20 mx-auto text-emerald-600 mb-4" />
        <h1 className="font-display text-3xl font-black tracking-tight">Commande confirmée !</h1>
        <p className="mt-3 text-muted-foreground">
          Merci pour votre confiance. Vous recevrez un email de confirmation à <strong>{status.customer_email}</strong>.
        </p>
        <div className="mt-6 p-4 rounded-lg bg-white text-left space-y-1">
          <p className="text-sm"><strong>Kit commandé :</strong> {status.kit_name}</p>
          <p className="text-sm"><strong>Total payé :</strong> {status.total_display}</p>
          <p className="text-sm"><strong>Référence :</strong> <span className="font-mono text-xs">{status.session_id}</span></p>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-2 text-xs">
          <div className="p-3 rounded bg-white flex flex-col items-center gap-1">
            <Package className="h-5 w-5 text-primary" />
            <span className="text-muted-foreground">Préparation sous 24h</span>
          </div>
          <div className="p-3 rounded bg-white flex flex-col items-center gap-1">
            <Truck className="h-5 w-5 text-secondary" />
            <span className="text-muted-foreground">Suivi Colissimo par email</span>
          </div>
        </div>
        <Link to="/kits"><Button className="mt-8 rounded-full">Voir d'autres kits</Button></Link>
      </Card>
    </div>
  );
}
